import React, { FC } from 'react';
import { Flex, Button, Text, Modal } from '@goodrx/matisse';

import { useToggledState } from 'app/hooks';
import { useSynapse } from 'app/lib/synaptik';
import { SpaceProps } from 'styled-system';
import { useImmer } from 'app/hooks';
import { SectionTitle } from 'app/components/SectionTitle';

interface SmsDisclaimerModalProps {
  open: boolean;
  onClose: () => void;
  loading: boolean;
  onClick: () => void;
}

const SmsDisclaimerModal: React.FC<SmsDisclaimerModalProps> = ({ open, onClose, loading, onClick }) => (
  <Modal open={open} onClose={onClose} title="Text a Coupon" minWidth={350}>
    <Modal.Body>
      <Flex flexDirection="column">
        <Text fontSize={14} mb={4} color="greyDarker">
          These messages are unencrypted and may be read by a third party. By texting yourself a coupon, you agree to
          receive coupons, refill reminders and promotional messages from HeyDoctor. You can opt out anytime by replying
          &quot;STOP&quot; to any message.
        </Text>

        <Button
          onClick={async () => {
            await onClick();
            onClose();
          }}
          loading={loading}
          block
          outline>
          Send Coupon
        </Button>
      </Flex>
    </Modal.Body>
  </Modal>
);

type ImmerState = Record<'sendingEmail' | 'sendingText', boolean>;

interface SendCouponCtaProps {
  visitPrescriptionId: number;
  title?: string;
}

export const SendCouponCta: FC<SendCouponCtaProps & SpaceProps> = ({ visitPrescriptionId, title, ...props }) => {
  const [sendCoupon] = useSynapse(({ prescriptions }) => [prescriptions.sendCoupon]);
  const [smsModalOpen, toggleSmsModal] = useToggledState(false);

  const [{ sendingEmail, sendingText }, setState] = useImmer<ImmerState>({
    sendingEmail: false,
    sendingText: false,
  });

  const handleClick = async ({ key, work }: { key: keyof ImmerState; work: () => Promise<any> }) => {
    try {
      setState({ [key]: true });
      await work();
    } catch {
      // empty
    } finally {
      setState({ [key]: false });
    }
  };

  return (
    <>
      <Flex {...props} flexDirection="column">
        {title && <SectionTitle>{title}</SectionTitle>}

        <Flex flexDirection={{ _: 'column', sm: 'row' }}>
          <Button variant="grey" onClick={() => toggleSmsModal()} block>
            Text Coupon
          </Button>

          <Button
            variant="grey"
            loading={sendingEmail}
            onClick={() =>
              handleClick({
                key: 'sendingEmail',
                work: () => sendCoupon(visitPrescriptionId, 'email'),
              })
            }
            block
            mt={{ _: 3, sm: 0 }}
            ml={{ _: 0, sm: 3 }}>
            Email Coupon
          </Button>
        </Flex>
      </Flex>
      <SmsDisclaimerModal
        open={smsModalOpen}
        onClose={toggleSmsModal}
        loading={sendingText}
        onClick={() =>
          handleClick({
            key: 'sendingText',
            work: () => sendCoupon(visitPrescriptionId, 'sms'),
          })
        }
      />
    </>
  );
};
